/**
 * AMIS Support Service
 * Quản lý tích hợp AMISSUPORT cho TaskGo
 * Thay thế LiveChat widget
 */
class AmisSupportService {
  constructor() {
    this.isLoaded = false;
    this.isInitialized = false;
    this.isChatVisible = false;
    this.unreadCount = 0;
    this.latestMessage = null;
    this.eventListeners = {};
    this.config = window._amisSupport || {};
  }

  /**
   * Load AMISSUPORT script
   */
  async loadScript() {
    if (this.isLoaded || !this.config.enabled) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      try {
        const script = document.createElement('script');
        script.src = this.config.scriptUrl;
        script.async = true;

        script.onload = () => {
          this.isLoaded = true;
          console.log('AMISSUPORT script loaded successfully');
          resolve();
        };

        script.onerror = () => {
          console.error('Failed to load AMISSUPORT script');
          reject(new Error('Failed to load AMISSUPORT script'));
        };

        document.head.appendChild(script);
      } catch (error) {
        console.error('Error loading AMISSUPORT script:', error);
        reject(error);
      }
    });
  }

  /**
   * Khởi tạo AMISSUPORT với user info
   */
  async initializeChat(userInfo = {}) {
    if (this.isInitialized || !this.config.enabled) {
      return;
    }

    try {
      await this.loadScript();

      // Khởi tạo với clientId và user info theo API đúng
      window.initAmisSupport({
        clientId: this.config.clientId,
        identityId: userInfo.userId || '',
        fullname: userInfo.userName || '',
        email: userInfo.userEmail || '',
        mobile: userInfo.mobile || '',
        company: userInfo.Company || '',
        companyCode: userInfo.CompanyCode || '',
        taxcode: userInfo.taxcode || '',
        companyId: userInfo.CompanyId || '',
        source: 'TASKGOV2_APP',
        viewMode: 'DESKTOP',
        showTrigger: false,
      });

      this.isInitialized = true;
      this.setupEventListeners();
      console.log('AMISSUPORT initialized successfully');
    } catch (error) {
      console.error('Failed to initialize AMISSUPORT:', error);
      throw error;
    }
  }

  /**
   * Setup event listeners cho unread count và latest message
   */
  setupEventListeners() {
    if (!window.AmisSupport?.ChatWindow) return;

    try {
      // Lắng nghe thay đổi visibility (mở/đóng chat)
      window.AmisSupport.ChatWindow.on('visibility_changed', (data) => {
        console.log('AMISSUPORT: Visibility changed:', data);
        if (
          data &&
          (data.visibility === 'maximized' || data.visibility === 'minimized')
        ) {
          this.emit('unreadCountChanged', 0);
          this.emit('messageReceived', null);
          if (data.visibility === 'maximized') {
            // Chat opened
            this.isChatVisible = true;
            console.log('AMISSUPORT: Chat window opened (maximized)');
            this.emit('chatOpened', data);
          } else if (data.visibility === 'minimized') {
            // Chat closed
            this.isChatVisible = false;
            console.log('AMISSUPORT: Chat window closed (minimized)');
            this.emit('chatClosed', data);
          }
        }
      });

      // Lắng nghe thay đổi số tin nhắn chưa đọc
      window.AmisSupport.ChatWindow.on('update_unread', (data) => {
        const { unread, lastMessage } = data;
        this.unreadCount = unread || 0;
        this.latestMessage = {
          content: lastMessage || '',
          time: new Date(),
        };

        this.emit('unreadCountChanged', this.unreadCount);
        this.emit('messageReceived', this.latestMessage);
      });
    } catch (error) {
      console.error('Error setting up AMISSUPORT event listeners:', error);
    }
  }

  /**
   * Hiển thị chat window
   */
  showChat() {
    if (!this.isInitialized || !window.AmisSupport?.ChatWindow) {
      console.warn('AMISSUPORT not initialized');
      return;
    }

    try {
      window.AmisSupport.ChatWindow.show();
      console.log('AMISSUPORT: Chat show() called');
    } catch (error) {
      console.error('Error showing AMISSUPORT chat:', error);
    }
  }

  /**
   * Ẩn chat window
   */
  hideChat() {
    if (!this.isInitialized || !window.AmisSupport?.ChatWindow) {
      return;
    }

    try {
      window.AmisSupport.ChatWindow.hide();
      console.log('AMISSUPORT: Chat hide() called');
    } catch (error) {
      console.error('Error hiding AMISSUPORT chat:', error);
    }
  }

  /**
   * Toggle chat window (manual implementation)
   */
  toggleChat() {
    if (!this.isInitialized || !window.AmisSupport?.ChatWindow) {
      console.warn('AMISSUPORT not initialized');
      return;
    }

    try {
      if (this.isChatVisible) {
        console.log('AMISSUPORT: Toggling chat - hiding (was visible)');
        window.AmisSupport.ChatWindow.hide();
      } else {
        console.log('AMISSUPORT: Toggling chat - showing (was hidden)');
        window.AmisSupport.ChatWindow.show();
      }
    } catch (error) {
      console.error('Error toggling AMISSUPORT chat:', error);
    }
  }

  /**
   * Format tin nhắn cho tooltip
   */
  getLatestMessageForTooltip() {
    if (!this.latestMessage) {
      return 'Không có tin nhắn mới';
    }

    const { content, time, sender } = this.latestMessage;
    const timeStr = this.formatTime(time);
    console.log('Latest message content:', content);
    if (content.length > 50) {
      return `${content.senderName}: ${content.content.substring(
        0,
        47
      )}... (${timeStr})`;
    }

    return `${content.senderName}: ${content.content} (${timeStr})`;
  }

  /**
   * Format thời gian
   */
  formatTime(time) {
    if (!time) return '';

    const date = new Date(time);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Vừa xong';
    if (diffMins < 60) return `${diffMins} phút trước`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} giờ trước`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} ngày trước`;
  }

  /**
   * Event system
   */
  on(eventName, callback) {
    if (!this.eventListeners[eventName]) {
      this.eventListeners[eventName] = [];
    }
    this.eventListeners[eventName].push(callback);
  }

  off(eventName, callback) {
    if (!this.eventListeners[eventName]) return;

    const index = this.eventListeners[eventName].indexOf(callback);
    if (index > -1) {
      this.eventListeners[eventName].splice(index, 1);
    }
  }

  emit(eventName, data) {
    if (!this.eventListeners[eventName]) return;

    this.eventListeners[eventName].forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in event listener for ${eventName}:`, error);
      }
    });
  }

  /**
   * Cleanup khi component bị destroy
   */
  destroy() {
    this.eventListeners = {};

    if (window.AmisSupport?.ChatWindow) {
      try {
        // Cleanup AMISSUPORT events
        window.AmisSupport.ChatWindow.off?.('visibility_changed');
        window.AmisSupport.ChatWindow.off?.('update_unread');
        console.log('AMISSUPORT: Events cleaned up');
      } catch (error) {
        console.error('Error cleaning up AMISSUPORT events:', error);
      }
    }
  }

  /**
   * Check xem service có sẵn sàng không
   */
  isReady() {
    return this.isInitialized && window.AmisSupport?.ChatWindow;
  }

  /**
   * Get current chat visibility state
   */
  getChatVisibility() {
    return this.isChatVisible;
  }
}

// Export singleton instance
export default new AmisSupportService();
